import type { LegalDocPair } from '../types'

/**
 * Política de facturación y reembolsos.
 *
 * Detalla el apartado 8 de los términos. Lo que dice del cobro sale de lo que el
 * servidor hace: se cobra por adelantado a través de Stripe, la renovación es
 * automática, cancelar detiene la siguiente renovación y el plan sigue hasta el
 * final del periodo pagado, y bajar de plan nunca borra contenido (sólo impide
 * crecer). Las cifras concretas de precio NO se escriben aquí: se muestran en la
 * pantalla de planes en el momento de comprar, para que no haya dos fuentes.
 */
export const BILLING: LegalDocPair = {
  en: {
    title: 'Billing and Refund Policy',
    summary: 'How paid plans are charged, renewed, cancelled and refunded.',
    intro: [
      'This Billing and Refund Policy is part of the [Terms of Service](doc:terms) and explains how we charge for paid plans and other paid services, and when we refund. Fees for selling through your Site are covered by the [Seller Terms](doc:sellers) and domain charges by the [Domain Terms](doc:domains).',
    ],
    sections: [
      {
        title: 'Plans and prices',
        blocks: [
          'We offer a free plan, {planStarter}, and paid plans, currently {planPro} and {planBusiness}, and we may add others. The price, the billing period and what each plan includes are shown on the Plans screen at the moment you buy, and that is the price you accept. Prices are in US dollars unless checkout shows another currency, and they do not include taxes.',
        ],
      },
      {
        title: 'How we charge',
        subs: [
          {
            title: 'Payment through Stripe',
            blocks: [
              'Payments are processed by Stripe. You authorize us, through Stripe, to charge your payment method for the plan you choose, for every renewal and for anything else you buy from us. We do not receive or store your full card number. Stripe emails your receipts and billing notices, such as a failed payment or an upcoming renewal, in your preferred language.',
            ],
          },
          {
            title: 'Billing periods',
            blocks: [
              'Monthly plans are billed every month in advance, and annual plans are billed once a year in advance. The period starts on the day you subscribe. If you move from the free plan to a paid plan, the charge is made immediately.',
            ],
          },
          {
            title: 'Automatic renewal',
            blocks: [
              'Your plan renews automatically for the same period, at the price in force at the time of renewal, until you cancel. We or Stripe may send you an email about renewals and receipts. Invoices and receipts are available in Billing.',
            ],
          },
          {
            title: 'Taxes',
            blocks: [
              'Prices do not include sales tax, VAT, GST or similar taxes. Unless checkout tells you that we collect a tax, you are responsible for any tax that applies to your purchase. If we are required to collect a tax, it is shown separately.',
            ],
          },
        ],
      },
      {
        title: 'Launch discount',
        blocks: [
          'During the beta, annual plans receive a launch discount of {betaPercent}% for the first {betaMonths} months. The discount, how it works and what happens after it ends are explained in the [Beta Program Terms](doc:beta). It does not apply to monthly billing.',
        ],
      },
      {
        title: 'Changing or cancelling your plan',
        subs: [
          {
            title: 'Cancelling',
            blocks: [
              'You can cancel at any time from Billing. Cancelling stops the next renewal. You keep your plan until the end of the period you already paid for, and then your Account moves to the free plan. We do not cancel or refund part of a period on your behalf.',
            ],
          },
          {
            title: 'What happens to your content',
            blocks: [
              'When a plan ends or you move to a plan with lower limits, we do not delete your content. Pages, collections, offers, blog posts and other items over the new limits remain and keep being served, but you cannot add more until you are within the limits or you upgrade. On the free plan, form messages received above the monthly allowance are stored but hidden until you upgrade or the count resets. Features that belong only to paid plans, such as removing the véltica badge, custom domain connection or extra team members, stop being available.',
            ],
          },
          {
            title: 'Upgrading',
            blocks: [
              'You can upgrade at any time. The charge for the change is shown at checkout before you confirm it.',
            ],
          },
        ],
      },
      {
        title: 'Refunds',
        subs: [
          {
            title: 'General rule',
            blocks: [
              'Fees already charged are not refundable, and neither is the unused part of a billing period or the time left on an annual plan, except in the cases below.',
            ],
          },
          {
            title: 'When we do refund',
            blocks: [
              {
                list: [
                  'where the law that applies to you requires it;',
                  'where we were unable to provide the service because of a fault of ours, for the affected period; and',
                  'where you were charged by mistake, for example twice or after cancelling in time, if you write to us within 30 days of the charge.',
                ],
              },
              'To ask for a refund, write to {email} with the email address of your Account and the charge. If we approve it, we refund the original payment method. Banks and Stripe may take several days to show it. If you paid in a currency other than US dollars, the refunded amount may differ from what you paid because of exchange rates we do not control.',
            ],
          },
          {
            title: 'Consumers in the EEA and the United Kingdom',
            blocks: [
              'If you are a consumer in the European Economic Area or the United Kingdom, you may have a right to withdraw from a purchase within 14 days. By asking us to start the service immediately, you agree that you may lose that right once the service has been fully provided, and that if you withdraw after the service has partly been provided, you will pay for what you used until then.',
            ],
          },
          {
            title: 'Domains and sales',
            blocks: [
              'Domain charges are covered by the [Domain Terms](doc:domains), and refunds of sales you make to your buyers are covered by the [Seller Terms](doc:sellers).',
            ],
          },
        ],
      },
      {
        title: 'Failed payments and disputes',
        subs: [
          {
            title: 'If a payment fails',
            blocks: [
              'If a payment fails, Stripe or we may retry it and ask you to update your payment method. If it cannot be collected, your paid features may be suspended and your Account may return to the free plan at the end of the period, without deleting your content. We may charge again any amount that remains unpaid.',
            ],
          },
          {
            title: 'Chargebacks',
            blocks: [
              'If you have a problem with a charge, please write to us first at {email}; we will do our best to solve it quickly. If you dispute a charge with your bank or card issuer without contacting us, we may suspend the Account while it is reviewed, and we will lock the transfer of any domain we granted with the plan that was charged. If the dispute is decided against you, we may recover the amount, the fees we pay and any related costs.',
            ],
          },
        ],
      },
      {
        title: 'Changes to prices and plans',
        blocks: [
          'We may change prices, plans and limits. We will tell you at least 30 days before a change that raises the price of your plan takes effect, and it will apply from your next renewal after that notice. If you do not agree, cancel before it takes effect. Prices you already paid for a period do not change during that period.',
        ],
      },
      {
        title: 'Questions',
        blocks: ['For any question about a charge, write to {email}.'],
      },
    ],
  },

  es: {
    title: 'Política de facturación y reembolsos',
    summary: 'Cómo se cobran, renuevan, cancelan y reembolsan los planes de pago.',
    intro: [
      'Esta Política de facturación y reembolsos forma parte de los [Términos de servicio](doc:terms) y explica cómo cobramos los planes de pago y otros servicios de pago, y cuándo reembolsamos. Las comisiones por vender desde tu Sitio están en los [Términos de ventas y pagos](doc:sellers) y los cargos de dominios en los [Términos de dominios](doc:domains).',
    ],
    sections: [
      {
        title: 'Planes y precios',
        blocks: [
          'Ofrecemos un plan gratuito, {planStarter}, y planes de pago, hoy {planPro} y {planBusiness}, y podemos añadir otros. El precio, el periodo de cobro y lo que incluye cada plan se muestran en la pantalla de Planes en el momento de comprar, y ese es el precio que aceptas. Los precios están en dólares estadounidenses salvo que al pagar se muestre otra moneda, y no incluyen impuestos.',
        ],
      },
      {
        title: 'Cómo cobramos',
        subs: [
          {
            title: 'Pago a través de Stripe',
            blocks: [
              'Los pagos los procesa Stripe. Nos autorizas, a través de Stripe, a cobrar tu método de pago por el plan que elijas, por cada renovación y por cualquier otra cosa que nos compres. No recibimos ni guardamos el número completo de tu tarjeta. Stripe te envía los recibos y avisos de facturación, como un pago fallido o una renovación próxima, en tu idioma preferido.',
            ],
          },
          {
            title: 'Periodos de cobro',
            blocks: [
              'Los planes mensuales se cobran cada mes por adelantado y los anuales una vez al año por adelantado. El periodo empieza el día que te suscribes. Si pasas del plan gratuito a uno de pago, el cargo se hace de inmediato.',
            ],
          },
          {
            title: 'Renovación automática',
            blocks: [
              'Tu plan se renueva automáticamente por el mismo periodo, al precio vigente en el momento de la renovación, hasta que cancelas. Nosotros o Stripe podemos enviarte un correo sobre renovaciones y recibos. Las facturas y recibos están disponibles en Facturación.',
            ],
          },
          {
            title: 'Impuestos',
            blocks: [
              'Los precios no incluyen impuestos sobre ventas, IVA, GST ni similares. Salvo que al pagar te digamos que cobramos un impuesto, eres responsable de cualquier impuesto que aplique a tu compra. Si debemos cobrar un impuesto, se muestra por separado.',
            ],
          },
        ],
      },
      {
        title: 'Descuento de lanzamiento',
        blocks: [
          'Durante la beta, los planes anuales reciben un descuento de lanzamiento de {betaPercent}% durante los primeros {betaMonths} meses. El descuento, cómo funciona y qué pasa cuando termina se explican en los [Términos del programa Beta](doc:beta). No aplica al cobro mensual.',
        ],
      },
      {
        title: 'Cambiar o cancelar tu plan',
        subs: [
          {
            title: 'Cancelar',
            blocks: [
              'Puedes cancelar en cualquier momento desde Facturación. Cancelar detiene la siguiente renovación. Conservas tu plan hasta el final del periodo ya pagado y después tu Cuenta pasa al plan gratuito. No cancelamos ni reembolsamos parte de un periodo por tu cuenta.',
            ],
          },
          {
            title: 'Qué pasa con tu contenido',
            blocks: [
              'Cuando termina un plan o pasas a uno con límites más bajos, no borramos tu contenido. Las páginas, colecciones, ofertas, entradas del blog y otros elementos que superen los nuevos límites se conservan y se siguen sirviendo, pero no puedes añadir más hasta estar dentro de los límites o mejorar de plan. En el plan gratuito, los mensajes de formularios recibidos por encima del cupo mensual se guardan pero quedan ocultos hasta que mejores de plan o se reinicie el recuento. Las funciones propias de los planes de pago, como quitar la insignia de véltica, conectar un dominio propio o tener más miembros de equipo, dejan de estar disponibles.',
            ],
          },
          {
            title: 'Mejorar de plan',
            blocks: [
              'Puedes mejorar de plan en cualquier momento. El cargo por el cambio se muestra al pagar, antes de que lo confirmes.',
            ],
          },
        ],
      },
      {
        title: 'Reembolsos',
        subs: [
          {
            title: 'Regla general',
            blocks: [
              'Las Tarifas ya cobradas no se reembolsan, ni tampoco la parte no usada de un periodo de cobro ni el tiempo que quede de un plan anual, salvo en los casos de abajo.',
            ],
          },
          {
            title: 'Cuándo sí reembolsamos',
            blocks: [
              {
                list: [
                  'cuando la ley que te aplica lo exija;',
                  'cuando no hayamos podido prestar el servicio por un fallo nuestro, por el periodo afectado; y',
                  'cuando se te haya cobrado por error, por ejemplo dos veces o después de cancelar a tiempo, si nos escribes dentro de los 30 días siguientes al cargo.',
                ],
              },
              'Para pedir un reembolso, escribe a {email} con el correo de tu Cuenta y el cargo. Si lo aprobamos, reembolsamos al método de pago original. Los bancos y Stripe pueden tardar varios días en reflejarlo. Si pagaste en una moneda distinta del dólar, el importe reembolsado puede diferir del pagado por los tipos de cambio, que no controlamos.',
            ],
          },
          {
            title: 'Consumidores del EEE y del Reino Unido',
            blocks: [
              'Si eres consumidor en el Espacio Económico Europeo o el Reino Unido, puedes tener derecho a desistir de una compra en 14 días. Al pedirnos que empecemos el servicio de inmediato, aceptas que puedes perder ese derecho cuando el servicio se haya prestado por completo, y que si desistes cuando el servicio se ha prestado en parte, pagarás lo que hayas usado hasta ese momento.',
            ],
          },
          {
            title: 'Dominios y ventas',
            blocks: [
              'Los cargos de dominios se rigen por los [Términos de dominios](doc:domains), y los reembolsos de las ventas que haces a tus compradores por los [Términos de ventas y pagos](doc:sellers).',
            ],
          },
        ],
      },
      {
        title: 'Pagos fallidos y disputas',
        subs: [
          {
            title: 'Si falla un pago',
            blocks: [
              'Si falla un pago, Stripe o nosotros podemos reintentarlo y pedirte que actualices tu método de pago. Si no se puede cobrar, tus funciones de pago pueden suspenderse y tu Cuenta puede volver al plan gratuito al final del periodo, sin borrar tu contenido. Podemos volver a cobrar cualquier importe que quede sin pagar.',
            ],
          },
          {
            title: 'Contracargos',
            blocks: [
              'Si tienes un problema con un cargo, escríbenos primero a {email}; haremos lo posible por resolverlo rápido. Si disputas un cargo con tu banco o emisor de tarjeta sin contactarnos, podemos suspender la Cuenta mientras se revisa, y bloquearemos la transferencia de cualquier dominio que hayamos concedido con el plan cobrado. Si la disputa se resuelve en tu contra, podemos recuperar el importe, las tarifas que paguemos y los costes relacionados.',
            ],
          },
        ],
      },
      {
        title: 'Cambios en precios y planes',
        blocks: [
          'Podemos cambiar precios, planes y límites. Te avisaremos al menos 30 días antes de que surta efecto un cambio que suba el precio de tu plan, y se aplicará desde tu siguiente renovación posterior a ese aviso. Si no estás de acuerdo, cancela antes de que surta efecto. Los precios que ya pagaste por un periodo no cambian durante ese periodo.',
        ],
      },
      {
        title: 'Preguntas',
        blocks: ['Para cualquier duda sobre un cargo, escribe a {email}.'],
      },
    ],
  },
}
